import type { ComponentProps, ComponentType } from "react";
import { Fragment } from "react/jsx-runtime";
import { t } from "ttag";

import weightLossGuaranteeImage from "@/assets/onboarding/weight-loss-guarantee.webp";
import { CHECKOUT_ID } from "@/features/onboarding/components/choose-products/selected-product";
import { PRODUCT_GROUP_CARDS_ID } from "@/features/onboarding/screens/treatment/choose-products";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { BILLING_FREQUENCY, CATEGORY_SLUG } from "@/shared/domains";
import type { SvgSlot } from "@/shared/ui";
import {
  Badge,
  Button,
  Heading,
  IPhoneIcon,
  JarOfPillsIcon,
  LAYOUT_SCROLL_VIEWPORT_ID,
  StethoscopeIcon,
} from "@/shared/ui";
import { NotesIcon } from "@/shared/ui/icons/documents/notes-icon";
import { BookIcon } from "@/shared/ui/icons/educative/book-icon";
import { CarrotIcon } from "@/shared/ui/icons/food/carrot-icon";
import { TagPriceIcon } from "@/shared/ui/icons/money/tag-icon";
import { formatPrice } from "@/shared/utils";
import { scrollToSection } from "@/shared/utils/dom";

interface IncludedItem {
  icon: ComponentType<ComponentProps<typeof SvgSlot>>;
  title: string;
  subtitle?: string;
}

export const WeightLossProgramCard = () => {
  const selectedProduct = useOnboardingStore(
    (state) =>
      state.selectedCategories.find(
        (category) => category.slug === CATEGORY_SLUG.WEIGHT_LOSS,
      )?.selectedProduct,
  );

  const selectedPrice = selectedProduct?.selectedPrice;
  const longestPlanPrice =
    selectedProduct?.prices.find(
      (p) => p.durationInMonths === BILLING_FREQUENCY.ANNUALLY,
    ) ?? selectedProduct?.prices.at(-1);

  const currentPricePerMonth = formatPrice(
    selectedPrice && selectedPrice.price / selectedPrice.durationInMonths,
  );

  const startingPricePerMonth = formatPrice(
    longestPlanPrice &&
      longestPlanPrice.price / longestPlanPrice.durationInMonths,
  );

  const includedItems: IncludedItem[] = [
    {
      icon: JarOfPillsIcon,
      title: t`Access to GLP-1 medication`,
      subtitle: t`Cost of medication is included`,
    },
    { icon: TagPriceIcon, title: t`No insurance necessary` },
    { icon: IPhoneIcon, title: t`Board-Certified doctor review` },
    { icon: StethoscopeIcon, title: t`1:1 Physician guidance` },
    {
      icon: NotesIcon,
      title: t`Metabolic report`,
      subtitle: t`Includes custom in-depth metabolic results`,
    },
    { icon: CarrotIcon, title: t`Sample meal plan based on metabolism` },
    { icon: BookIcon, title: t`Nutrition report` },
  ];

  const handleScroll = () => {
    const idToScroll = selectedProduct ? CHECKOUT_ID : PRODUCT_GROUP_CARDS_ID;

    scrollToSection(idToScroll, LAYOUT_SCROLL_VIEWPORT_ID);
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl bg-secondary-50 p-5">
      <Badge size="small" color="dark-secondary">
        {t`Are you ready?`}
      </Badge>

      <Heading level={2} color="primary" size="large" weight="medium">
        {t`Start your journey today`}
      </Heading>

      <div className="flex flex-col gap-4 overflow-hidden rounded-t-[46px] rounded-b-2xl bg-white p-8 pt-0">
        <div className="-mx-8 bg-secondary-500 px-8 py-2 text-center text-xs font-medium text-white">
          {t`Prescriptions start at just ${startingPricePerMonth}/mo - no insurance needed`}
        </div>

        <Heading
          level={3}
          color="primary"
          size="medium"
          weight="medium"
          align="center"
        >
          {t`The most effective weight loss program is right here`}
        </Heading>

        <div className="flex flex-col gap-3">
          <p className="font-semibold text-primary-950">{t`What is included?`}</p>

          <div className="flex flex-col gap-4">
            {includedItems.map(({ icon, title, subtitle }) => (
              <IncludedItemRow
                key={title}
                icon={icon}
                title={title}
                subtitle={subtitle}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 py-3 text-center">
          <p className="text-2xl font-semibold text-secondary-500">{t`Prescribed for just ${currentPricePerMonth}/mo`}</p>

          <p className="text-primary-950">{t`No contracts, cancel anytime. Medication included.`}</p>
        </div>

        <Button size="lg" onClick={handleScroll}>
          {t`Checkout`}
        </Button>

        <WeightLossGuarantee />
      </div>
    </div>
  );
};

const IncludedItemRow = ({
  icon: Icon = Fragment,
  title,
  subtitle,
}: IncludedItem) => (
  <div className="flex gap-2">
    <Icon className="mt-1 size-4 text-secondary-300" />

    <div className="flex flex-col gap-1">
      <p className="text-primary-950">{title}</p>

      {subtitle && <p className="text-secondary-500">{subtitle}</p>}
    </div>
  </div>
);

const WeightLossGuarantee = () => (
  <div className="flex gap-3 py-3">
    <img
      alt={t`Weight Loss Guarantee`}
      src={weightLossGuaranteeImage}
      className="size-24 shrink-0 rounded-sm bg-secondary-100 object-contain"
    />

    <div className="flex flex-col gap-1 text-primary-950">
      <p className="font-semibold">{t`Weight Loss Guarantee`}</p>

      <p>{t`If you do not lose weight by the end of your complete program, we give you all of your money back. It's that simple!`}</p>
    </div>
  </div>
);
